import { gql } from '@apollo/client';

export const PRODUCT_UNIVERSAL_PRODUCT = gql`
  fragment ProductUniversalProductSummary on UniversalProduct {
    id
    title
    description
    url
    storeName
    salePrice {
      valueInMinorUnits
      floatingPointDecimalString
      currency {
        code
        places
      }
    }
    listPrice {
      valueInMinorUnits
      floatingPointDecimalString
      currency {
        code
        places
      }
    }
    rating {
      id
      averageRating
      reviewsCount
    }
    photos {
      url
    }
  }
`;

export const PRODUCT_EXTERNAL_SUMMARY = gql`
  fragment ProductExternalSummary on ProductExternal {
    id
    title
    description
    photos {
      url
    }
    rating {
      id
      averageRating
      reviewsCount
    }
    sellers {
      id
      name
      url
      fullPrice {
        valueInMinorUnits
        floatingPointDecimalString
        currency {
          code
          places
        }
      }
      salePrice {
        valueInMinorUnits
        floatingPointDecimalString
        currency {
          code
          places
        }
      }
    }
  }
`;

export const PRODUCT_EXTERNAL_OFFER_SUMMARY = gql`
  fragment ProductExternalOfferSummary on ProductExternalOffer {
    productId
    productTitle
    productThumbnail {
      url
    }
    productRating {
      id
      averageRating
      reviewsCount
    }
    sellerName
    sellerUrl
    sellerFullPrice {
      valueInMinorUnits
      floatingPointDecimalString
      currency {
        code
        places
      }
    }
    sellerSalePrice {
      valueInMinorUnits
      floatingPointDecimalString
      currency {
        code
        places
      }
    }
  }
`;

export const PRODUCT_CATALOG_ITEM_SEARCH_SUMMARY = gql`
  fragment ProductCatalogItemSearchSummary on ProductCatalogItem {
    ...CatalogProductSharedDisplayFields

    collectionDisplayInfos {
      displayInfoId
      pdpSectionType
    }

    tilePresentation {
      ...ProductCatalogItemTilePresentation
    }

    group {
      id
      ...CatalogProductTileViewDimensions
    }
  }
`;

export const PRODUCT_CATALOG_CATEGORY_SUMMARY = gql`
  fragment ProductCatalogCategorySummary on ProductCatalogCategory {
    id
    publicUrlPath
    title
    isParentType
    thumbnail {
      url
    }
    directItemCount
    tileItemImages {
      url
    }
    curationData {
      description
    }
  }
`;

export const PRODUCT_CATALOG_CATEGORY_DETAILS = gql`
  fragment ProductCatalogCategoryDetails on ProductCatalogCategory {
    ...ProductCatalogCategorySummary
    children {
      ...ProductCatalogCategorySummary
    }
    lineage {
      ...ProductCatalogCategorySummary
    }
  }
`;

export const PRODUCT_CATALOG_CATEGORY_TREE_ROOT_FRAGMENT = gql`
  fragment ProductCatalogCategoryTreeRoot on ProductCatalogCategory {
    ...ProductCatalogCategorySummary
    heroText
    children {
      ...ProductCatalogDepartment
    }
  }
`;

export const PRODUCT_CATALOG_DEPARTMENT = gql`
  fragment ProductCatalogDepartment on ProductCatalogCategory {
    ...ProductCatalogCategorySummary
    heroText
    children {
      ...ProductCatalogCategory
    }
  }
`;

export const PRODUCT_CATALOG_CATEGORY = gql`
  fragment ProductCatalogCategory on ProductCatalogCategory {
    ...ProductCatalogCategorySummary
    children {
      ...ProductCatalogSubcategory
    }
  }
`;

export const PRODUCT_CATALOG_SUBCATEGORY = gql`
  fragment ProductCatalogSubcategory on ProductCatalogCategory {
    ...ProductCatalogCategorySummary
    children {
      ...ProductCatalogCategoryDetails
    }
  }
`;
export const PRODUCT_TILE_VIEW_DIMENSION_FRAGMENT = gql`
  fragment ProductTileViewDimension on ProductCatalogItemGroupDimension {
    id
    title
    options {
      id
      groupDimension {
        id
        title
        swatchType
      }
      displayString
      swatchType
      swatchUrl
      swatchColor {
        hex
        rgba
        isLight
      }
    }
    swatchType
  }
`;
export const PRODUCT_CATALOG_ITEM_SUMMARY_FRAGMENT = gql`
  fragment ProductCatalogItemSummary on ProductCatalogItem {
    id
    title
    itemType
    brand {
      id
      name
    }
    photos {
      id
      url
    }
    parentCategories {
      id
    }
    subCategories {
      ...ProductCatalogCategorySummary
    }
    productUrl
    price {
      currency {
        code
        places
      }
      valueInMinorUnits
      floatingPointDecimalString
    }
    fullPrice {
      currency {
        code
        places
      }
      valueInMinorUnits
      floatingPointDecimalString
    }
    isFullPrice
    groupTileViewDimension: group {
      tileViewDimension {
        ...ProductTileViewDimension
      }
    }
  }
`;

export const PRODUCT_CATALOG_ITEM_FRAGMENT = gql`
  fragment ProductCatalogItem on ProductCatalogItem {
    id
    title
    itemType
    brand {
      id
      name
      publicUrlPath
    }
    retailer {
      name
    }
    whyWeLoveIt
    shortDescription
    longDescription
    productUrl
    photos {
      id
      url
    }
    subCategories {
      ...ProductCatalogCategoryDetails
    }
    price {
      currency {
        code
        places
      }
      valueInMinorUnits
      floatingPointDecimalString
    }
    storefrontInfo {
      storefrontStatus
      canAddToCart
      canAddToRegistry
    }
    isFullPrice
  }
`;

export const PRODUCT_CATALOG_ITEM_DIMENSIONS_FRAGMENT = gql`
  fragment ProductCatalogItemDimensions on ProductCatalogItem {
    groupDimensionOptions {
      id
      displayString
      swatchType
      swatchColor {
        hex
        rgba
        isLight
      }
      swatchUrl
    }
    group {
      id
      tileViewDimension {
        ...ProductTileViewDimension
      }
      groupDimensions {
        title
        options {
          id
          displayString
          swatchType
          swatchColor {
            hex
            rgba
            isLight
          }
          swatchUrl
        }
      }
      items {
        ...ProductCatalogItem
        groupDimensionOptions {
          id
          displayString
          swatchType
          swatchColor {
            hex
            rgba
            isLight
          }
          swatchUrl
        }
      }
    }
  }
`;

export const PRODUCT_CATALOG_ITEM_DETAILS_FRAGMENT = gql`
  fragment ProductCatalogItemDetails on ProductCatalogItem {
    ...ProductCatalogItem
    ...ProductCatalogItemDimensions
  }
`;

export const PRODUCT_CATEGORY_BREADCRUMBS_FRAGMENT = gql`
  fragment ProductCategoryBreadcrumbs on ProductCatalogCategory {
    id
    title
    publicUrlPath
    lineage {
      id
      title
    }
  }
`;

export const BLOG_ARTICLE_FRAGMENT = gql`
  fragment BlogArticle on BlogArticle {
    title
    eyebrow
    description
    url
    imageUrl
  }
`;

export const CATALOG_CATEGORY_CHILDREN_SUMMARY = gql`
  fragment CatalogCategoryChildrenSummary on ProductCatalogCategory {
    id
    title
    publicUrlPath
  }
`;

export const CATALOG_SEARCH_PAGE_INFO = gql`
  fragment ProductCatalogSearchPageInfo on ProductCatalogSearchPageInfo {
    hasNextPage
    hasPreviousPage
    startCursor
    endCursor
    totalCount
  }
`;
